import React, { Component, Fragment } from "reactn";
import { Icon } from "@material-ui/core";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Update from "mdi-material-ui/Update";
import CalendarRange from "mdi-material-ui/CalendarRange";
import Numeric from "mdi-material-ui/Numeric";
import Star from "mdi-material-ui/Star";

import { _ } from "js/vendor";
import { ds } from "js/core/models/dataService";
import { app } from "js/namespaces";
import getLogger, { LogGroup } from "js/core/logger";
import { trackActivity } from "js/core/utilities/utilities";
import { SearchThrottleWait } from "common/constants";
import AppController from "js/core/AppController";
import { Slide } from "js/core/models/slide";
import { ShowConfirmationDialog, ShowDialogAsync, ShowErrorDialog, ShowInputDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import { RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideGeneralNotice, SharedSlideNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { Gap } from "js/react/components/Gap";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import { Breadcrumb } from "js/editor/PresentationLibrary/PresentationLibrary";

import { teamSlidesDataService } from "../DataServices";
import { FilterDropDown } from "./Components/FilterDropDown";
import {
    InnerWhiteFrameContainer,
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer
} from "./Components/SearchBox";
import { ThumbnailGrid } from "./Components/ThumbnailGrid";
import { SortMenu } from "./Components/SortMenu";
import { TeamSlideThumbnail } from "./Components/TeamSlideThumbnail";

const logger = getLogger(LogGroup.TEAMS);

const filterOptions = [
    {
        id: "all_content",
        name: "All Content"
    },
    {
        id: "name",
        name: "Title"
    },
    {
        id: "description",
        name: "Description"
    },
    {
        id: "text",
        name: "Slide Content"
    },
    {
        id: "tags",
        name: "Tags"
    },
    {
        id: "template",
        name: "Template"
    },
];

export class TeamTemplatePane extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isLoaded: false,
            query: this.global.searchSlidesQuery ?? "",
            sort: null,
            tags: [{ id: "all_tags", name: "All Tags" }],
            selectedContent: "all_content",
            selectedTag: "all_tags",
            searchResults: [],
        };

        this.scrollRef = React.createRef();
        this.fetchSearchResultsThrottled = _.throttle(this.fetchSearchResults, SearchThrottleWait, { leading: false });
        this._fetchSearchResultsPromise = Promise.resolve();

        if (props.organizationId) {
            this.defaultTeam = ds.teams.defaultTeamForOrg(props.organizationId);
        }
    }

    get sortOptions() {
        return [{
            value: "name", label: "Name", icon: <SortAlphabeticalVariant />
        }, {
            value: "ratedByCount", label: "Rating", reverse: true, icon: <Star />
        }, {
            value: "presentationCount", label: "Popularity", reverse: true, icon: <Numeric />
        }, {
            value: "createdAt", label: "Created At", reverse: true, icon: <CalendarRange />
        }, {
            value: "modifiedAt", label: "Modified At", reverse: true, icon: <Update />
        }];
    }

    componentDidMount() {
        this.subscribeForLibraryItemsChanges();
        this.fetchSearchResults();
    }

    componentWillUnmount() {
        this.unsubscribeFromLibraryItemsChanges();
    }

    componentDidUpdate(prevProps, prevState) {
        const { organizationId, isTemplate } = this.props;
        const { query, selectedContent, selectedTag } = this.state;

        if (
            prevState.query !== query ||
            prevState.selectedContent !== selectedContent ||
            prevState.selectedTag !== selectedTag ||
            prevProps.organizationId !== organizationId ||
            prevProps.isTemplate !== isTemplate
        ) {
            this.setState({ isLoaded: false });

            if (prevProps.organizationId !== organizationId) {
                this.unsubscribeFromLibraryItemsChanges();
                this.defaultTeam = organizationId ? ds.teams.defaultTeamForOrg(organizationId) : null;
                this.subscribeForLibraryItemsChanges();
            }

            this.fetchSearchResultsThrottled();
        }
    }

    setStateAsync(stateUpdate) {
        return new Promise(resolve => this.setState(stateUpdate, resolve));
    }

    subscribeForLibraryItemsChanges() {
        if (this.defaultTeam?.libraryItems) {
            this.defaultTeam.libraryItems.on("update", this.fetchSearchResults);
        }
    }

    unsubscribeFromLibraryItemsChanges() {
        if (this.defaultTeam?.libraryItems) {
            this.defaultTeam.libraryItems.off("update", this.fetchSearchResults);
        }
    }

    fetchSearchResults = () => {
        this._fetchSearchResultsPromise = this._fetchSearchResultsPromise.then(async () => {
            const { organizationId, isManageLibrary, isTemplate } = this.props;
            const { selectedContent, query, selectedTag } = this.state;

            if (!organizationId) {
                await this.setStateAsync({
                    searchResults: [],
                    tags: [],
                    isLoaded: true
                });
                return;
            }

            let searchResults = await teamSlidesDataService.search({
                fullTextSearchQuery: query,
                organizationId,
                tag: selectedTag !== "all_tags" ? selectedTag : undefined,
                fullTextSearchByFields: (selectedContent && selectedContent !== "all_content") ? [selectedContent] : [],
                size: 200
            });

            if (!isManageLibrary) {
                searchResults = searchResults.filter(({ isDisabled }) => !isDisabled);
            }

            searchResults = searchResults.filter(slide => !!slide.isTemplate === isTemplate);

            const tagsMap = {};
            searchResults.forEach(({ tags }) => {
                tags.forEach(tag => {
                    if (!tagsMap[tag]) {
                        tagsMap[tag] = 1;
                    } else {
                        tagsMap[tag]++;
                    }
                });
            });
            const tags = [{ id: "all_tags", name: "All Tags" }, ...Object.entries(tagsMap).map(([tag, tagCount]) => ({ id: tag, name: `${tag} (${tagCount})` }))];
            if (!tags.some(({ id }) => id === selectedTag)) {
                tags.push({ id: selectedTag, name: `${selectedTag} (0)` });
            }

            await this.setStateAsync({
                searchResults,
                tags,
                isLoaded: true
            });
        });

        return this._fetchSearchResultsPromise;
    }

    handleItemSelected = async item => {
        const { isManageLibrary, onSelected, resourceType } = this.props;

        if (isManageLibrary) {
            await ShowDialogAsync(TeamSlideDialog, {
                organizationId: this.props.organizationId,
                libraryItemId: item.libraryItemId,
                closePane: this.props.closePane,
                resourceType,
            });

            this.fetchSearchResults();
        } else {
            const props = {
                ...ds.selection.presentation.getAnalytics(),
                slide_id: item.id,
            };
            trackActivity("Presentation", "LibrarySlideAdded", null, null, props);
            onSelected(item);
        }
    }

    handleSelectTag = event => {
        this.setState({ selectedTag: event.target.value });
    }

    handleSearch = query => {
        this.setState({ query });
    }

    handleSort = sort => {
        this.setState({ sort });
    }

    handleFilterChange = event => {
        this.setState({ selectedContent: event.target.value });
    }

    handleAddItem = async () => {
        const { resourceType } = this.props;

        AppController.showSlideEditor({ resourceType });
    }

    handleEditSlide = async item => {
        const { resourceType } = this.props;

        AppController.showSlideEditor({ slideId: item.id, resourceType });
    }

    copySlide = async (itemModel, updateProps) => {
        const {
            name,
            tags,
            id,
            description,
            isDisabled,
            teamId,
            isTemplate,
            template_id,
        } = itemModel;

        const slideOld = new Slide({ id });
        await slideOld.load();

        let slideModel = slideOld.attributes;
        delete slideModel.id;
        delete slideModel.libraryItemId;
        delete slideModel.thumbnails;

        const slideNew = new Slide(slideModel, {
            userId: app.userId,
            autoSync: false
        });
        await slideNew.load();

        let newModel = {
            contentId: slideNew.id,
            name,
            tags,
            description,
            type: "slides",
            isDisabled,
            teamId,
            isTemplate,
            template_id,
            ...updateProps,
        };

        await AppController.currentTeam.libraryItems.createLibraryItem(
            newModel,
            slideNew,
            AppController.orgId,
        );
    }

    handleCopySlide = async item => {
        // isTemaplte = slide template
        // !isTemplate = shared slide
        if (item.isTemplate) {
            await this.copySlide(item, {
                isTemplate: false,
                name: "Copy of " + item.name
            });
            this.fetchSearchResults();
            ShowWarningDialog({
                title: "A copy of this slide is now available to your team as a finished, uneditable Shared Slide.",
                icon: "/images/ui/icons/done-check.gif"
            });
        } else {
            await this.copySlide(item, {
                isTemplate: true,
                name: "Copy of " + item.name,
                isDisabled: false
            });
            this.fetchSearchResults();
            ShowWarningDialog({
                title: "A copy of this slide is now available to your team as an editable Slide Template.",
                icon: "/images/ui/icons/done-check.gif"
            });
        }
    }

    handleDuplicateSlide = async item => {
        let name = await ShowInputDialog({
            title: "Enter a name for the copied slide",
            value: "Copy of " + item.name
        });

        // Convert string array to object map
        let tags = item.tags.reduce((obj, tag) => {
            obj[tag] = true;
            return obj;
        }, {});

        if (name) {
            await this.copySlide(item, {
                name,
                tags,
            });

            this.fetchSearchResults();
        }
    }

    handleDeleteSlide = async item => {
        let backboneLibraryItem = item.libraryItem;

        if (item.isUsedInPresentations) {
            if (await ShowConfirmationDialog({
                title: `Sorry, you can't delete a ${this.props.title} that is currently used in presentations.`,
                message: `If you want to prevent this ${this.props.title} from being used in any additional presentations, you can hide the slide.`,
                okButtonLabel: "Hide Slide"
            })) {
                backboneLibraryItem.update({ isDisabled: true });
                const props = {
                    slide_id: backboneLibraryItem.get("contentId"),
                };
                trackActivity(this.props.trackActivity, "SlideDisabled", null, null, props);
                this.fetchSearchResults();
            }
            return;
        }

        ShowConfirmationDialog({
            title: `Are you sure you want to delete this ${this.props.title}?`,
            message: `Deleting a ${this.props.title} permanently removes it from your ${this.props.title} library and prevents it from being used in any future presentations.`,
            buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
            okButtonLabel: "PERMANENTLY DELETE TEAM SLIDE",
            acceptCallback: async () => {
                this.setState(prevState => {
                    return {
                        ...prevState,
                        isLoading: true
                    };
                });
                try {
                    await backboneLibraryItem.destroy();
                    const props = {
                        slide_id: backboneLibraryItem.get("contentId"),
                    };
                    trackActivity(this.props.trackActivity, "SlideDeleted", null, null, props);
                    this.fetchSearchResults();
                } catch (err) {
                    logger.error(err, "[TeamTemplatePane] backboneLibraryItem.destroy() failed", { backboneLibraryItemId: backboneLibraryItem?.id });
                    ShowErrorDialog({
                        error: "An error occurred while deleting the Team Slide.",
                        message: (
                            <Fragment>
                                <p>
                                    <strong>Error:</strong> {err.message}
                                </p>
                                <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                            </Fragment>
                        )
                    });
                }
            }
        });
    }

    render() {
        const {
            isManageLibrary,
            organizationId,
            title,
            resourceType
        } = this.props;
        const {
            query,
            selectedTag,
            selectedContent,
            tags
        } = this.state;

        if (!organizationId) {
            return (<UIPane>
                {resourceType === "slide-templates" && <SharedSlideNotice />}
                {resourceType !== "slide-templates" && <NoTeamSlideNotice />}
            </UIPane>
            );
        }

        let panelStyles, searchStyles, scrollStyles;
        if (!isManageLibrary) {
            panelStyles = {
                paddingTop: 4,
                paddingRight: 20,
                paddingBottom: 0,
                paddingLeft: 20
            };
            searchStyles = {
                position: "initial"
            };
        } else {
            scrollStyles = {
                height: "calc(100% - 49px)"
            };
        }

        return (
            <UIPane>
                <SearchBarContainer className="search-container">
                    {isManageLibrary &&
                        <Fragment>
                            <Breadcrumb>{title}s</Breadcrumb>
                            <RoundIconButton onClick={this.handleAddItem}><Icon>add</Icon></RoundIconButton>
                            <Gap size={60} />
                        </Fragment>
                    }
                    <SearchBarInnerContainer>
                        <SlideSearchInput
                            query={query}
                            onSearch={this.handleSearch}
                            placeholder="Search team slides…"
                        />
                        <FilterDropDown
                            onChange={this.handleFilterChange}
                            selectedFilter={selectedContent}
                            filterOptions={filterOptions}
                        />
                        <FilterDropDown
                            onChange={this.handleSelectTag}
                            selectedFilter={selectedTag}
                            filterOptions={tags}
                        />
                        <SortMenu
                            disabled={query.length > 0}
                            sortOptions={this.sortOptions}
                            onSetSort={this.handleSort}
                        />
                    </SearchBarInnerContainer>
                </SearchBarContainer>
                <UIPaneResultsContainer>
                    {this.renderSlides()}
                </UIPaneResultsContainer>
            </UIPane>
        );
    }

    renderSlides() {
        const {
            isManageLibrary,
            resourceType
        } = this.props;
        let {
            isLoaded,
            query,
            searchResults,
            sort,
        } = this.state;

        if (!isLoaded) {
            return <Spinner />;
        }

        if (!searchResults.length) {
            if (!query) {
                if (resourceType === "slide-templates") {
                    if (isManageLibrary) return <SharedSlideNotice />;
                    else {
                        // We added this because we we are not in the manageing library
                        // We should present to any user this default message regardless of
                        // their role
                        return (<SharedSlideGeneralNotice />);
                    }
                }
                return <NoTeamSlideNotice />;
            } else {
                return <NoMatchNotice />;
            }
        }

        const sortedResults = sort
            ? _.sortBy(searchResults, item => {
                if (typeof item[sort.field] === "string") {
                    return item[sort.field].toLowerCase().trim();
                } else {
                    return item[sort.field];
                }
            })
            : searchResults;
        if (sort?.reverse) {
            sortedResults.reverse();
        }

        return (<InnerWhiteFrameContainer>
            <ThumbnailGrid
                items={sortedResults}
                columns={3}
                thumbnailClass={TeamSlideThumbnail}
                style={{}}
                thumbnailProps={{
                    isManageLibrary,
                    onEditSlide: this.handleEditSlide,
                    onDeleteSlide: this.handleDeleteSlide,
                    onCopySlide: this.handleCopySlide,
                    onDuplicateSlide: this.handleDuplicateSlide
                }}
                onItemDoubleClick={this.handleItemSelected}
            />
        </InnerWhiteFrameContainer>);
    }
}
